import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import { useCallback, useEffect, useRef, useState } from "react";

import { db } from "integrations/firebase/firestore";

import { registrationCommandSchema, type CommandStatus } from "./schemas";

export type RegistrationCommandType =
  "REGISTER_FOR_EVENT" | "WITHDRAW_FROM_EVENT";

export type CommandState =
  | { status: "idle" }
  | { status: "submitting" }
  | { status: "watching"; commandStatus: CommandStatus }
  | { status: "succeeded"; type: RegistrationCommandType }
  | { status: "failed"; error: string };

export function useRegistrationCommand() {
  const [state, setState] = useState<CommandState>({ status: "idle" });
  const unsubRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    return () => {
      unsubRef.current?.();
    };
  }, []);

  const submit = useCallback(
    async (
      type: RegistrationCommandType,
      eventId: string,
      uid: string,
      registrationSecret?: string,
    ) => {
      unsubRef.current?.();
      unsubRef.current = null;
      setState({ status: "submitting" });

      try {
        const docRef = await addDoc(collection(db, "registration_requests"), {
          type,
          requestedBy: uid,
          requestedAt: serverTimestamp(),
          eventId,
          status: "REQUESTED",
          // An empty offering would still be shape-checked by the rules, so
          // the key travels only when there is a code to offer. Withdrawals
          // never carry one.
          ...(registrationSecret ? { registrationSecret } : {}),
        });

        setState({ status: "watching", commandStatus: "REQUESTED" });

        const unsub = onSnapshot(
          doc(db, "registration_requests", docRef.id),
          (snap) => {
            if (!snap.exists()) return;
            const result = registrationCommandSchema.safeParse(snap.data());
            if (!result.success) return;

            const cmd = result.data;
            if (cmd.status === "SUCCEEDED") {
              unsub();
              unsubRef.current = null;
              // The toast has to tell the two outcomes apart, so the state
              // remembers which command it was that succeeded.
              setState({ status: "succeeded", type });
            } else if (cmd.status === "FAILED") {
              unsub();
              unsubRef.current = null;
              setState({
                status: "failed",
                error: cmd.error ?? "Request failed",
              });
            } else {
              setState({ status: "watching", commandStatus: cmd.status });
            }
          },
          (err) => {
            unsub();
            unsubRef.current = null;
            setState({
              status: "failed",
              error:
                err instanceof Error ? err.message : "Failed to watch command",
            });
          },
        );

        unsubRef.current = unsub;
      } catch (err) {
        setState({
          status: "failed",
          error:
            err instanceof Error ? err.message : "Failed to submit request",
        });
      }
    },
    [],
  );

  const reset = useCallback(() => {
    unsubRef.current?.();
    unsubRef.current = null;
    setState({ status: "idle" });
  }, []);

  return { state, submit, reset };
}
